import axios, { AxiosRequestConfig } from 'axios';
import ApiV2 from './api-v2';

interface AuthTokens {
  authToken: string;
  idToken: string;
}
export default class Api {
  private static authTokens: AuthTokens;
  private static readonly axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_DOMAIN,
  });

  public static setAuthResult(authTokens: AuthTokens): void {
    this.authTokens = authTokens;

    this.axiosInstance.interceptors.request.use(this.addAuthHeaders);
  }

  private static addAuthHeaders = (config: AxiosRequestConfig): AxiosRequestConfig => {
    const headers = config.headers as Record<string, string>;
    headers['Authorization'] = `Bearer ${this.authTokens.authToken}`;
    headers['Identity'] = `Token ${this.authTokens.idToken}`;

    // Forward impersonation header so the legacy Lambda backend can resolve
    // the impersonated user's tenantId. Backend gates this on super_admin role.
    const impersonateEmail = ApiV2.getImpersonateEmail();
    if (impersonateEmail) {
      headers['x-impersonate-email'] = impersonateEmail;
    }

    return config;
  };

  public static async get<T>(url: string, params?: Record<string, string>): Promise<T> {
    const response = await this.axiosInstance.get(url, {
      params,
    });
    return response.data as T;
  }

  public static async delete<T>(url: string): Promise<T> {
    const response = await this.axiosInstance.delete(url);
    return response.data as T;
  }

  /**
   * Downloads a file with optional progress tracking
   * @param url - The URL to download from
   * @param responseHandler - Callback to handle the downloaded blob
   * @param onProgress - Optional progress callback that receives percentage (0-100)
   * @reason Added progress tracking for better download UX
   */
  public static async getFile(
    url: string,
    responseHandler: (data: Blob) => void,
    onProgress?: (progressPercentage: number) => void,
  ): Promise<void> {
    await axios
      .create()
      .get(url, {
        responseType: 'blob',
        headers: {},
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        onDownloadProgress: onProgress
          ? (progressEvent: { loaded: number; total?: number }) => {
              if (progressEvent.total) {
                const progressPercentage = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total,
                );
                onProgress(progressPercentage);
              }
            }
          : undefined,
      })
      .then((response) => responseHandler(new Blob([response.data])));
  }

  public static async patch<T>(url: string, value: T): Promise<void> {
    await this.axiosInstance.patch(url, value);
  }

  public static async post<T>(url: string, value: T): Promise<T> {
    return (await this.axiosInstance.post(url, value)).data as T;
  }

  public static async put<T>(url: string, value: T): Promise<T> {
    return (await this.axiosInstance.put(url, value)).data as T;
  }

  public static async putFile<T>(
    url: string,
    data: T,
    onProgress: (progress: number) => void,
  ): Promise<void> {
    await axios.create().put(url, data, {
      onUploadProgress: (progressEvent: { loaded: number; total: number }) => {
        onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      },
    });
  }
}
